import React, { FC, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Paper from "@mui/material/Paper";
import MuiButton from "@mui/material/Button";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import Modal from "@/components/Modal";
import Button from "@/components/Button";
import Select from "@/components/Select";
import { useAppTheme } from "@/utils/theme";
import { useUserStore } from "@/hooks/useUserStore";
import { useActionsCatalog } from "@/hooks/useActions";
import { Action } from "@/domain/actions.interface";
import {
  RolePayload,
  RoleSubTypeCode,
  RoleType,
  RoleTypeCode,
} from "@/domain/rol.interface";
import roleValidationSchema, { RoleFormData } from "./validation.schema";

type RoleModalProps = {
  open: boolean;
  onClose: () => void;
  role?: RoleType;
  onSave: (data: RolePayload) => Promise<void>;
  isPending: boolean;
};

const DEFAULT_VALUES: RoleFormData = {
  name: "",
  actions: [],
  status: "ACTIVE",
  type: "",
  subType: "",
};

const RoleModal: FC<RoleModalProps> = ({
  open,
  onClose,
  role,
  onSave,
  isPending,
}) => {
  const { t } = useTranslation("common");
  const theme = useAppTheme();
  const { userInfo } = useUserStore();
  const isEdit = !!role;

  const { actionsCatalog, isLoadingActionsCatalog } = useActionsCatalog(open);

  const availableActions: Action[] = useMemo(
    () => actionsCatalog?.list || [],
    [actionsCatalog]
  );

  const methods = useForm<RoleFormData>({
    resolver: yupResolver(roleValidationSchema(t)),
    mode: "all",
    defaultValues: DEFAULT_VALUES,
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = methods;

  useEffect(() => {
    if (role) {
      reset({
        name: role.name,
        actions: role.actions || [],
        status: (role.status as "ACTIVE" | "INACTIVE") || "ACTIVE",
        type: (role.type as RoleTypeCode) || "",
        subType: (role.subType as RoleSubTypeCode) || "",
      });
    } else {
      reset(DEFAULT_VALUES);
    }
  }, [role, reset]);

  const onSubmit = async (data: RoleFormData) => {
    const currentUserId = userInfo?._id as string;
    const payload: RolePayload = {
      name: data.name.trim(),
      actions: (data.actions || []) as string[],
      ...(data.status ? { status: data.status as RolePayload["status"] } : {}),
      ...(data.type ? { type: data.type as RoleTypeCode } : {}),
      ...(data.subType ? { subType: data.subType as RoleSubTypeCode } : {}),
      ...(isEdit ? { updatedBy: currentUserId } : { createdBy: currentUserId }),
    };
    await onSave(payload);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      isPending={isPending}
      title={isEdit ? "Edit Role" : "New role"}
      theme={theme}
      size={{
        width: "100%",
        minHeight: 480,
        maxHeight: 720,
        maxWidth: 760,
      }}
      content={
        <FormProvider {...methods}>
          <Box sx={{ width: "100%", pt: "12px" }}>
            <Grid container spacing={"20px"}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="role-name"
                      label="Name"
                      variant="outlined"
                      required
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      error={!!errors.name}
                      helperText={errors.name ? errors.name.message : null}
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <Select
                      handleChange={(value) => field.onChange(value)}
                      data={[
                        { key: "ACTIVE", value: "Active" },
                        { key: "INACTIVE", value: "Inactive" },
                      ]}
                      value={field.value as string}
                      label="Status"
                      id="role-status"
                      error={!!errors.status}
                      errorMessage={errors.status?.message || ""}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="type"
                  control={control}
                  render={({ field }) => (
                    <Select
                      handleChange={(value) => field.onChange(value)}
                      data={[
                        {
                          key: "STANDARD_SYSTEM_ROLE",
                          value: "Standard system role",
                        },
                        {
                          key: "STANDARD_ACCOUNT_ROLE",
                          value: "Standard account role",
                        },
                        { key: "CUSTOM_ROLE", value: "Custom role" },
                      ]}
                      value={field.value as string}
                      label="Type"
                      id="role-type"
                      showClearOption
                      error={!!errors.type}
                      errorMessage={errors.type?.message || ""}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="subType"
                  control={control}
                  render={({ field }) => (
                    <Select
                      handleChange={(value) => field.onChange(value)}
                      data={[
                        {
                          key: "IN-HOUSE_PROVIDER",
                          value: "In-house provider",
                        },
                        { key: "SUPPORT_STAFF", value: "Support staff" },
                        { key: "OFFICER", value: "Officer" },
                      ]}
                      value={field.value as string}
                      label="Sub type"
                      id="role-sub-type"
                      showClearOption
                      error={!!errors.subType}
                      errorMessage={errors.subType?.message || ""}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="actions"
                  control={control}
                  render={({ field }) => {
                    const selectedIds = (field.value as string[]) || [];
                    const selectedObjects = availableActions.filter((a) =>
                      selectedIds.includes(a._id)
                    );
                    const allSelected =
                      availableActions.length > 0 &&
                      selectedIds.length === availableActions.length;

                    const handleSelectAll = () => {
                      field.onChange(availableActions.map((a) => a._id));
                    };

                    const handleClearAll = () => {
                      field.onChange([]);
                    };

                    return (
                      <Autocomplete
                        multiple
                        disableCloseOnSelect
                        options={availableActions}
                        loading={isLoadingActionsCatalog}
                        disabled={isPending}
                        getOptionLabel={(option) =>
                          option.name || option._id || ""
                        }
                        isOptionEqualToValue={(option, value) =>
                          option._id === value._id
                        }
                        value={selectedObjects}
                        onChange={(_e, selected) => {
                          field.onChange(selected.map((a) => a._id));
                        }}
                        PaperComponent={({ children, ...paperProps }) => (
                          <Paper {...paperProps}>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                p: 1,
                                borderBottom: 1,
                                borderColor: "divider",
                              }}
                              onMouseDown={(e) => e.preventDefault()}
                            >
                              <MuiButton
                                size="small"
                                variant="text"
                                disabled={allSelected}
                                onClick={handleSelectAll}
                              >
                                Select all
                              </MuiButton>
                              <MuiButton
                                size="small"
                                variant="text"
                                disabled={selectedIds.length === 0}
                                onClick={handleClearAll}
                              >
                                Clear options
                              </MuiButton>
                            </Box>
                            {children}
                          </Paper>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Actions"
                            placeholder="Select actions"
                            required
                            error={!!errors.actions}
                            helperText={
                              (errors.actions as { message?: string })
                                ?.message || ""
                            }
                          />
                        )}
                      />
                    );
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </FormProvider>
      }
      actions={
        <Box sx={{ display: "flex", gap: "10px" }}>
          <Button
            variantType="cancel"
            onClick={onClose}
            disabled={isPending}
            label="CANCEL"
          />
          <Button
            variantType="save"
            onClick={() => handleSubmit(onSubmit)()}
            disabled={isPending}
            isLoading={isPending}
            label="SAVE"
          />
        </Box>
      }
    />
  );
};

export default RoleModal;
